import { useEffect, useState } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { AdminHeading, Card, inputCls } from "@/components/admin/ui";

export default function FaqEditor() {
  const { reload } = useSite();
  const [lang, setLang] = useState("en");
  const [faqs, setFaqs] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/content").then(({ data }) => setFaqs((lang === "hi" ? data.hi?.faqs : data.en.faqs) || [])).catch((e) => toast.error(formatApiError(e)));
  }, [lang]);

  const upd = (i, k, v) => setFaqs((arr) => arr.map((f, j) => (j === i ? { ...f, [k]: v } : f)));
  const move = (i, d) => setFaqs((arr) => {
    const a = [...arr]; const j = i + d;
    if (j < 0 || j >= a.length) return a;
    [a[i], a[j]] = [a[j], a[i]];
    return a;
  });
  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/content", { data: { faqs: faqs.filter((f) => f.q.trim() && f.a.trim()) } }, { params: { lang } }); await reload(); toast.success(lang === "en" ? "FAQs saved · Hindi will auto-update shortly" : "Hindi FAQs saved"); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-faqs">
      <AdminHeading title="FAQs" subtitle="Shown on the Home and Learn pages, and used by Prana to answer questions.">
        <div className="flex gap-2">
          <div className="inline-flex p-1 rounded-full bg-white border text-xs font-bold" data-testid="faq-lang-switch">
            {[["en", "English"], ["hi", "हिंदी"]].map(([k, l]) => (
              <button key={k} onClick={() => setLang(k)} className={`px-3 py-1.5 rounded-full ${lang === k ? "bg-[var(--leaf)] text-white" : ""}`} data-testid={`faq-lang-${k}`}>{l}</button>
            ))}
          </div>
          <button onClick={() => setFaqs((a) => [...a, { q: "", a: "" }])} className="btn-ghost !py-2.5 text-sm" data-testid="faq-add-btn"><Plus size={16} /> Add</button>
          <button onClick={save} disabled={busy} className="btn-leaf !py-2.5 text-sm" data-testid="faq-save-btn">{busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save</button>
        </div>
      </AdminHeading>
      <div className="space-y-3">
        {faqs.map((f, i) => (
          <Card key={i} className="p-4 flex gap-3" data-testid={`faq-edit-${i}`}>
            <div className="flex flex-col gap-1">
              <button onClick={() => move(i, -1)} className="p-1.5 rounded hover:bg-[var(--leaf-soft)]" aria-label="Move up" data-testid={`faq-up-${i}`}><ArrowUp size={14} /></button>
              <button onClick={() => move(i, 1)} className="p-1.5 rounded hover:bg-[var(--leaf-soft)]" aria-label="Move down" data-testid={`faq-down-${i}`}><ArrowDown size={14} /></button>
            </div>
            <div className="flex-1 space-y-2">
              <input value={f.q} onChange={(e) => upd(i, "q", e.target.value)} placeholder="Question" className={`${inputCls} font-semibold`} data-testid={`faq-q-${i}`} />
              <textarea rows={2} value={f.a} onChange={(e) => upd(i, "a", e.target.value)} placeholder="Answer" className={inputCls} data-testid={`faq-a-${i}`} />
            </div>
            <button onClick={() => setFaqs((a) => a.filter((_, j) => j !== i))} className="p-2 h-fit rounded-lg text-red-600 hover:bg-red-50" aria-label="Remove" data-testid={`faq-remove-${i}`}><Trash2 size={15} /></button>
          </Card>
        ))}
      </div>
    </div>
  );
}
